// ...existing code...
<template>
  <Transition name="fade">
    <div class="fixed inset-0 z-[100000] flex items-center justify-center">
      <!-- Click-outside backdrop is mouse-only by design; keyboard users close via the Cancel button or Esc -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <div class="fixed inset-0 bg-black/40" @click="closeModal"></div>

      <div class="bg-white rounded-md shadow-lg w-full max-w-lg z-50 p-6">
        <h3 class="text-lg font-medium mb-4">{{ title }}</h3>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Reasoning</label>
          <textarea
            v-model="reasoning"
            rows="4"
            class="w-full border border-gray-300 rounded-md p-2"
            placeholder="Enter reason why this order is being put on hold"
          />
          <p v-if="error" class="text-sm text-red-600 mt-2">{{ error }}</p>
        </div>

        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 bg-white border rounded-md text-gray-700 hover:bg-gray-50"
            @click="closeModal"
            :disabled="saving"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-[#219f84] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            @click="submit"
            :disabled="saving"
          >
            <span v-if="saving" class="inline-block w-4 h-4 border-2 border-white rounded-full border-r-transparent animate-spin mr-2"></span>
            {{ props.mode === 'resume' ? 'Resume Order' : 'Put On Hold' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';

const props = defineProps<{
  orderItemId: number | string;
  mode?: 'put' | 'resume';
  title?: string;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'success', data?: any): void;
}>();

// Local state
const reasoning = ref('');
const saving = ref(false);
const error = ref<string | null>(null);

const defaultTitle = computed(() => (props.mode === 'resume' ? 'Resume Order' : 'Put Order On Hold'));
const title = computed(() => props.title ?? defaultTitle.value);

watch(
  () => props.mode,
  () => {
    // clear previous state when mode changes
    reasoning.value = '';
    error.value = null;
  }
);

const closeModal = () => {
  reasoning.value = '';
  error.value = null;
  emits('close');
};

const submit = async () => {
  error.value = null;
  // Require reasoning for both put and resume per request
  if (props.mode !== 'resume' && (!reasoning.value || !reasoning.value.toString().trim())) {
    error.value = 'Reasoning is required';
    return;
  }

  saving.value = true;
  try {
    const on_hold_flag = props.mode === 'resume' ? false : true;
    const payload = {
      on_hold: on_hold_flag,
      on_hold_reason: reasoning.value.toString().trim(),
      order_item_id: props.orderItemId,
    };
    const response = await http.post(`order/toggle-onhold-status/${props.orderItemId}`, payload);
    const resData = response.data;
    showToast(resData?.message ?? (on_hold_flag ? 'Order put on hold' : 'Order resumed'), 'success');
    emits('success', resData);
    closeModal();
  } catch (err) {
    handleResponseException(err);
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
/* minimal styling to match existing modals */
</style>
